import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BlockStack, Button, Icon, Text } from '@shopify/polaris';
import { CancelSmallMinor } from '@shopify/polaris-icons';
import { getWithExpiry, setWithExpiry } from '../../utils/LocalStorageExpiry';
import { DiscountKind, SubscriptionPlan } from '../../pages/SubscriptionPage/SubscriptionPlan';
import { RECOMMENDED, formatPrice, getPlanOffer } from '../../pages/SubscriptionPage/plans';
import { showsHalloweenOffer } from './HalloweenOfferBanner';
import './BFCMSaleBanner.css';

// The Early Bird BFCM Deal 2026: 50% off the first month of a plan change until November 21. Each store is
// assigned A or B once. A sees the poster with code BFCM2026A, B the ticket with BFCM2026B, so the codes'
// redemptions compare the two designs. Free stores see the Early Bird BFCM Offer instead until it ends.
export const OFFER_ENDS = new Date(2026, 10, 21, 23, 59, 59);
const DISMISSED_KEY = 'dismissed_bfcmSale2026';
const VARIANT_KEY = 'bfcm_ab_variant';
const DISMISS_FOR = 7 * 24 * 60 * 60 * 1000;
const DAY = 24 * 60 * 60 * 1000;

const assignVariant = () => {
  let variant = localStorage.getItem(VARIANT_KEY);
  if (variant !== 'A' && variant !== 'B') {
    variant = Math.random() < 0.5 ? 'A' : 'B';
    localStorage.setItem(VARIANT_KEY, variant);
  }
  return variant;
};

// The plan the offer is shown on: the recommended plan, or Unlimited for stores already on it. Unlimited stores
// have nothing to upgrade to.
const pitchedPlan = (currentPlan) =>
  currentPlan < RECOMMENDED.plan ? RECOMMENDED.plan : currentPlan === RECOMMENDED.plan ? SubscriptionPlan.Unlimited : null;

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Counted in calendar days, so the last day says "today" rather than "1 day left".
export const deadlineText = (now) => {
  const days = Math.round((startOfDay(OFFER_ENDS) - startOfDay(now)) / DAY);
  return days === 0 ? 'Ends today' : days === 1 ? 'Ends tomorrow' : `Ends Nov 21 · ${days} days left`;
};

// "Business is $12.99 for the first month instead of $25.99, after your 14-day free trial."
const priceLine = ({ name, price, promo, trialDays }) =>
  `${name} is ${formatPrice(promo.price)} for the first month instead of ${formatPrice(price)}${
    trialDays ? `, after your ${trialDays}-day free trial` : ''
  }.`;

const Dismiss = ({ onClick }) => (
  <button type="button" className="bfcm-dismiss" onClick={onClick} aria-label="Dismiss Early Bird BFCM offer">
    <Icon source={CancelSmallMinor} tone="inherit" />
  </button>
);

// A: one huge number on black.
const Poster = ({ offer, deadline, onClaim, onDismiss }) => (
  <section className="bfcm-poster" aria-label="Early Bird BFCM offer">
    <div className="bfcm-poster__figure" aria-hidden="true">
      50<span>%</span>
    </div>
    <div className="bfcm-poster__copy">
      <span className="bfcm-kicker">Early Bird BFCM on Disco</span>
      <h2 className="bfcm-poster__title">Half price on your first month of any upgrade</h2>
      <p className="bfcm-poster__body">{priceLine(offer)}</p>
    </div>
    <div className="bfcm-poster__cta">
      <Button size="large" onClick={onClaim}>Claim 50% off</Button>
      <span className="bfcm-poster__deadline">{deadline}</span>
    </div>
    <Dismiss onClick={onDismiss} />
  </section>
);

// B: the code as a ticket, with the discount on a torn-off stub.
const Ticket = ({ offer, code, onClaim, onDismiss }) => (
  <section className="bfcm-ticket" aria-label="Early Bird BFCM offer">
    <div className="bfcm-ticket__stub">
      <span className="bfcm-kicker">Early Bird BFCM</span>
      <span className="bfcm-ticket__big">−50%</span>
      <span className="bfcm-ticket__small">first month</span>
    </div>
    <div className="bfcm-ticket__main">
      <BlockStack gap="200">
        <Text as="h2" variant="headingMd">Your Early Bird BFCM code</Text>
        <span className="bfcm-ticket__code">{code}</span>
        <Text as="p" tone="subdued">{`Half off your first month on any plan. ${priceLine(offer)} Valid until November 21.`}</Text>
      </BlockStack>
      <Button variant="primary" size="large" onClick={onClaim}>Use code</Button>
    </div>
    <Dismiss onClick={onDismiss} />
  </section>
);

const BFCMSaleBanner = ({ meta, now = new Date() }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => !!getWithExpiry(DISMISSED_KEY));
  const [variant] = useState(assignVariant);
  const plan = meta && pitchedPlan(meta.SubscriptionPlan);
  if (dismissed || !plan || now > OFFER_ENDS || showsHalloweenOffer(meta, now)) return null;

  const code = `BFCM2026${variant}`;
  const discount = { code, kind: DiscountKind.Percent, value: 50, durationMonths: 1, appliesToPlans: [] };
  const offer = getPlanOffer(plan, { currentPlan: meta.SubscriptionPlan, trialUsedForPlans: meta.TrialUsedForPlans, discount });

  const onClaim = () => navigate(`/subscription/${code}`);
  const onDismiss = () => {
    setDismissed(true);
    setWithExpiry(DISMISSED_KEY, 'true', DISMISS_FOR);
  };

  return variant === 'B' ? (
    <Ticket offer={offer} code={code} onClaim={onClaim} onDismiss={onDismiss} />
  ) : (
    <Poster offer={offer} deadline={deadlineText(now)} onClaim={onClaim} onDismiss={onDismiss} />
  );
};

export default BFCMSaleBanner;
